import React, { useMemo, useState } from 'react';

import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BarChart } from '@mui/x-charts/BarChart';
import { startOfYear, format } from 'date-fns';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import PageHeader from '../../components/PageHeader';
import MeetingDetailDialog from '../MeetingsPage/MeetingDetailDialog';
import WaterfallDrillDown from '../MeetingsPage/WaterfallDrillDown';
import SurveyForm from '../SurveyPage/SurveyForm';
import { useUser } from '../../contexts/UserContext';
import { CALENDAR_DATA_QUERY, USER_COCKPIT_STATS_QUERY, USER_WATERFALL_QUERY } from '../../graphql/meetings';

const GET_ACTIVE_QUESTIONNAIRE = gql`
  query getActiveQuestionnaire {
    activeQuestionnaire {
      id
      version
      title
      description
      questions {
        key
        text
        type
        order
        options
      }
    }
  }
`;

const GET_SURVEY_FOR_MEETING = gql`
  query getSurveyForMeeting($meetingId: Int!) {
    meeting(id: $meetingId) {
      id
      domain
      companyId
      title
      meetingDate
      surveyResponses {
        id
        respondentId
        answers
        datetimeCreated
      }
    }
  }
`;

const SUBMIT_SURVEY_RESPONSE = gql`
  mutation submitSurveyResponse($meetingId: Int!, $answers: JSONString!) {
    submitSurveyResponse(meetingId: $meetingId, answers: $answers) {
      ok
      surveyResponse {
        id
        datetimeCreated
      }
      error
    }
  }
`;

// Meeting type colors — same as MeetingsCalendar
const INITIAL_COLOR = '#3F5D66';
const FOLLOWUP_COLOR = '#9e9e9e';
const INTERNAL_COLOR = '#d5d5d5';

function mapMeetingEvents(meetings: any[]) {
  return meetings.map((m: any) => {
    const isInternal = !m.domain && !m.companyId;
    const isInitial = m.callType === 'initial';

    let bgColor: string;
    let textColor = '#fff';
    if (isInternal) {
      bgColor = INTERNAL_COLOR;
      textColor = '#666';
    } else if (isInitial) {
      bgColor = INITIAL_COLOR;
    } else {
      bgColor = FOLLOWUP_COLOR;
    }

    const companyLabel = m.companyName ? ` [${m.companyName}]` : '';
    const displayTitle = (m.title || m.domain || 'Meeting') + companyLabel;

    return {
      id: String(m.id),
      title: displayTitle,
      start: m.meetingDate,
      end: m.endTime || undefined,
      backgroundColor: bgColor,
      borderColor: bgColor,
      textColor,
      extendedProps: m,
    };
  });
}

// ── Stat card ───────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  color = '#3F5D66',
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color?: string;
  onClick?: () => void;
}) {
  return (
    <Paper
      sx={{
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderLeft: `4px solid ${color}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
        '&:hover': onClick ? { boxShadow: 3 } : {},
      }}
      onClick={onClick}
    >
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 600, color }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

// ── Survey fill form (inside dialog) ────────────────────────────────

function SurveyFillView({
  meetingId,
  onBack,
  onCompleted,
}: {
  meetingId: number;
  onBack: () => void;
  onCompleted: () => void;
}) {
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: meetingData, loading: meetingLoading } = useQuery(GET_SURVEY_FOR_MEETING, {
    variables: { meetingId },
  });

  const { data: questionnaireData, loading: questionnaireLoading } = useQuery(GET_ACTIVE_QUESTIONNAIRE);

  const [submitSurvey, { loading: submitLoading }] = useMutation(SUBMIT_SURVEY_RESPONSE, {
    onCompleted: data => {
      if (data.submitSurveyResponse.ok) {
        setSnackbar({ open: true, message: 'Survey submitted!', severity: 'success' });
        setTimeout(onCompleted, 800);
      } else {
        setSnackbar({
          open: true,
          message: data.submitSurveyResponse.error || 'Submission failed',
          severity: 'error',
        });
      }
    },
    onError: err => {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    },
  });

  const handleSubmit = (answers: Record<string, unknown>) => {
    submitSurvey({ variables: { meetingId, answers: JSON.stringify(answers) } });
  };

  if (meetingLoading || questionnaireLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const meeting = meetingData?.meeting;
  const questionnaire = questionnaireData?.activeQuestionnaire;

  if (!meeting) return <Alert severity="warning">Meeting not found.</Alert>;
  if (!questionnaire) return <Alert severity="warning">No active questionnaire.</Alert>;

  const hasExisting = meeting.surveyResponses?.length > 0;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small" sx={{ mb: 2 }}>
        Back to list
      </Button>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {meeting.title || 'Call Assessment'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {meeting.domain}
            {meeting.meetingDate && ` · ${format(new Date(meeting.meetingDate), 'PPP')}`}
          </Typography>
        </CardContent>
      </Card>

      {hasExisting ? (
        <Alert severity="info">Survey already submitted for this meeting.</Alert>
      ) : (
        <SurveyForm questions={questionnaire.questions} onSubmit={handleSubmit} loading={submitLoading} />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ── Pending surveys dialog ──────────────────────────────────────────

function PendingSurveysDialog({
  open,
  onClose,
  surveys,
  onSurveyCompleted,
}: {
  open: boolean;
  onClose: () => void;
  surveys: any[];
  onSurveyCompleted: () => void;
}) {
  const [activeMeetingId, setActiveMeetingId] = useState<number | null>(null);

  const handleClose = () => {
    setActiveMeetingId(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {activeMeetingId ? 'Fill Survey' : 'Pending Surveys'}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {activeMeetingId ? (
          <SurveyFillView
            meetingId={activeMeetingId}
            onBack={() => setActiveMeetingId(null)}
            onCompleted={() => {
              setActiveMeetingId(null);
              onSurveyCompleted();
            }}
          />
        ) : surveys.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            All surveys completed — nice work!
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ pt: 1 }}>
            {surveys.map((s: any) => (
              <Paper
                key={s.meetingId}
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {s.companyName || s.domain || s.title || 'Meeting'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.meetingDate ? new Date(s.meetingDate).toLocaleDateString() : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  sx={{ bgcolor: '#3F5D66', '&:hover': { bgcolor: '#334D56' }, flexShrink: 0, ml: 1 }}
                  onClick={() => setActiveMeetingId(s.meetingId)}
                >
                  Fill Survey
                </Button>
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Calendar with tabs ──────────────────────────────────────────────

function TabbedCalendar({ userEmail }: { userEmail: string | null }) {
  const [tab, setTab] = useState(0);
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);

  const queryOverrides = tab === 0 ? (userEmail ? { calendarSourceId: userEmail } : {}) : { originationsOnly: true };

  const variables = dateRange
    ? { dateFrom: dateRange.start, dateTo: dateRange.end, ...queryOverrides }
    : { dateFrom: new Date().toISOString(), dateTo: new Date().toISOString() };

  const { data, refetch } = useQuery(CALENDAR_DATA_QUERY, {
    variables,
    skip: !dateRange,
    fetchPolicy: 'cache-and-network',
  });

  const events = mapMeetingEvents(data?.meetingsCalendarData ?? []);

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ mb: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
      >
        <Tab label="My Calendar" />
        <Tab label="Team" />
      </Tabs>
      <FullCalendar
        key={tab}
        plugins={[timeGridPlugin, dayGridPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'timeGridWeek,timeGridDay',
        }}
        events={events}
        datesSet={dateInfo => {
          setDateRange({ start: dateInfo.start.toISOString(), end: dateInfo.end.toISOString() });
        }}
        eventClick={info => setSelectedMeeting(info.event.extendedProps)}
        height="auto"
        slotMinTime="07:00:00"
        slotMaxTime="20:00:00"
        allDaySlot={false}
        weekends={false}
      />
      <MeetingDetailDialog
        open={!!selectedMeeting}
        onClose={() => {
          setSelectedMeeting(null);
          refetch();
        }}
        meeting={selectedMeeting}
      />
    </Box>
  );
}

// ── User waterfall ──────────────────────────────────────────────────

function UserWaterfall({ userId }: { userId: number }) {
  const dateFrom = useMemo(() => startOfYear(new Date()), []);
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownLabel, setDrillDownLabel] = useState('');

  const { data, loading } = useQuery(USER_WATERFALL_QUERY, {
    variables: { userId, dateFrom: dateFrom.toISOString() },
  });

  const bars = data?.userWaterfall?.bars ?? [];
  const funnelPct = data?.userWaterfall?.funnelPercentage ?? 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (bars.length === 0 || bars.every((b: any) => b.count === 0)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No validated companies yet this year.
      </Typography>
    );
  }

  const labels = bars.map((b: any) => b.label);
  const counts = bars.map((b: any) => b.count);

  return (
    <Box>
      <BarChart
        xAxis={[{ scaleType: 'band', data: labels }]}
        series={[{ data: counts, label: 'Companies', color: '#3F5D66' }]}
        height={280}
        onItemClick={(_event, itemData) => {
          if (itemData.dataIndex !== undefined && bars[itemData.dataIndex]) {
            setDrillDownLabel(bars[itemData.dataIndex].label);
            setDrillDownOpen(true);
          }
        }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Funnel conversion: {Math.round(funnelPct)}%
      </Typography>
      <WaterfallDrillDown
        open={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        barLabel={drillDownLabel}
        dateFrom={dateFrom.toISOString()}
        dateTo={new Date().toISOString()}
      />
    </Box>
  );
}

// ── Main cockpit page ───────────────────────────────────────────────

export default function Home() {
  const { userId, userEmail } = useUser();
  const [surveysDialogOpen, setSurveysDialogOpen] = useState(false);

  const {
    data: cockpitData,
    loading: cockpitLoading,
    refetch: refetchCockpit,
  } = useQuery(USER_COCKPIT_STATS_QUERY, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const stats = cockpitData?.userCockpitStats;

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      <PageHeader title={`Origination Cockpit ${stats?.userName ?? ''}`} variant="compact" />

      {/* ── Stat cards ── */}
      {cockpitLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<CheckCircleOutlineIcon />}
              label="Validated (7 days)"
              value={stats?.validatedCompanies7d ?? 0}
              color="#3F5D66"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssignmentIcon />}
              label="Surveys Answered"
              value={stats?.surveysAnswered ?? 0}
              color="#4E6694"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<PendingActionsIcon />}
              label="Surveys Pending"
              value={stats?.surveysPendingCount ?? 0}
              color={stats?.surveysPendingCount > 0 ? '#FF564F' : '#9e9e9e'}
              onClick={() => setSurveysDialogOpen(true)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<TrendingUpIcon />}
              label="Completion Rate"
              value={
                stats
                  ? `${Math.round(((stats.surveysAnswered ?? 0) / Math.max((stats.surveysAnswered ?? 0) + (stats.surveysPendingCount ?? 0), 1)) * 100)}%`
                  : '—'
              }
              color="#3F5D66"
            />
          </Grid>
        </Grid>
      )}

      {/* ── Pipeline funnel — full width below stat cards ── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          My Pipeline Funnel
          <Chip label="YTD" size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.7rem' }} />
        </Typography>
        {userId ? (
          <UserWaterfall userId={userId} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Loading user...
          </Typography>
        )}
      </Paper>

      {/* ── Calendar ── */}
      <Paper sx={{ p: 2 }}>
        <TabbedCalendar userEmail={userEmail} />
      </Paper>

      {/* ── Pending surveys dialog ── */}
      <PendingSurveysDialog
        open={surveysDialogOpen}
        onClose={() => setSurveysDialogOpen(false)}
        surveys={stats?.surveysPending ?? []}
        onSurveyCompleted={() => refetchCockpit()}
      />
    </Box>
  );
}
